import format from "date-fns/format";
import * as QRCode from "qrcode";
import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { BottomSheet } from "react-spring-bottom-sheet";

import { Container } from "../../components/Container";
import useAsyncMemo from "../../hooks/useAsyncMemo";
import useCompetition from "../../hooks/useCompetition";

export default function DashboardScreen() {
  const { competitionUUID } = useParams();
  const { competition } = useCompetition(competitionUUID);
  const navigate = useNavigate();

  const enterUrl = `https://comps.griptonite.io/${competitionUUID}/enter`;

  const [entryQRCodeUrl] = useAsyncMemo(
    () => QRCode.toDataURL(enterUrl),
    [competitionUUID]
  );

  const judgeKey = competition?.judgeKey;

  const [selectedQR, setSelectedQR] = useState<"ENTRY" | "JUDGE" | null>(null);

  const judgeUrl = `https://comps.griptonite.io/${competitionUUID}/judge?key=${judgeKey}`;

  const [judgeQRCodeUrl] = useAsyncMemo(
    () => (judgeKey ? QRCode.toDataURL(judgeUrl) : Promise.resolve("")),
    [competitionUUID, judgeKey]
  );

  const urlsMap = { ENTRY: enterUrl, JUDGE: judgeUrl };

  const copyLinkToClipboard = (linkType: typeof selectedQR) => {
    const url = linkType && urlsMap[linkType];
    if (!url) {
      return;
    }
    navigator.clipboard.writeText(url);
  };

  if (!competition) {
    return (
      <Container className="d-flex align-items-center justify-content-center vh-100">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!competition?.canJudge) {
    navigate(`../judge/onboarding`, {
      state: { next: `/${competitionUUID}/dashboard` },
      replace: true,
    });
  }

  return (
    <Container>
      <h1>{competition.name}</h1>
      <p>
        Start time:{" "}
        <strong>
          {format(new Date(competition.startTime), "HH:mm, do MMMM yyyy")}
        </strong>
        <br />
        End time:{" "}
        <strong>
          {format(new Date(competition.endTime), "HH:mm, do MMMM yyyy")}
        </strong>
      </p>
      <h2>Links</h2>
      <p>
        <Button onClick={() => setSelectedQR("ENTRY")}>
          Entry / live scorecard QR code
        </Button>
      </p>
      <p>
        <Button onClick={() => setSelectedQR("JUDGE")}>Judging QR code</Button>
      </p>
      <hr />
      <h2>Admin tools</h2>
      <h4>Setup</h4>
      <p>
        <Button href={`/${competitionUUID}/order-routes`} target="_blank">
          Assign numbers to routes
        </Button>
      </p>
      <h4>Entry and scoring</h4>
      <p>
        <Button onClick={() => navigate(`../entries`)}>Manage entries</Button>
        &nbsp;&nbsp;&nbsp;
        <Button href={`/${competitionUUID}/enter/competitors`} target="_blank">
          Add a competitor
        </Button>
      </p>
      <p></p>
      <p>
        <Button
          href={`https://api.griptonite.io/competitions/${competitionUUID}/entries/bulk.html`}
          target="_blank"
        >
          Bulk entry scoring editor
        </Button>
      </p>
      <h4>Downloads</h4>
      <p>
        <Button
          href={`http://api.griptonite.io/auth/login?next=/competitions/${competitionUUID}/entries.xls`}
          target="_blank"
        >
          Download results/entries spreadsheet
        </Button>
      </p>

      <BottomSheet open={selectedQR !== null}>
        <div
          style={{
            height: "calc(50vh - 100px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h1>{selectedQR === "ENTRY" ? "Entry" : "Judging"}</h1>
          <img
            src={selectedQR === "ENTRY" ? entryQRCodeUrl : judgeQRCodeUrl}
            style={{ height: "30vh", width: "auto" }}
          />
          <div className="mb-4">
            <Button
              variant="outline-primary"
              onClick={() => copyLinkToClipboard(selectedQR)}
            >
              Copy link
            </Button>
            &nbsp;&nbsp;
            <Button
              variant="outline-secondary"
              onClick={() => setSelectedQR(null)}
            >
              Close
            </Button>
          </div>
        </div>
      </BottomSheet>
    </Container>
  );
}
